/**
 * 세미나 목록 조건 — 주소(쿼리) ↔ 화면 상태 ↔ 서버 파라미터 (docs/seminar-filter-mobile-plan.md 1·2장).
 *
 * 주소: `type`(career|general) · `category` · `target` · `format` · `free=1` · `sort=startSoon` · `search`
 * 서버(`GET /seminars`): `seminarType` · `categories` · `levels` · `formats` · `freeOnly=true` · `sort` · `search`
 * - 전체 탭은 늘 `sort=careerFirst`(커리어 먼저), 다른 정렬을 고르면 `careerFirst,startSoon` 처럼 뒤에 붙인다.
 * - 정렬 값: `startSoon`(시작 임박) · `deadlineSoon`(모집 마감 임박) · `popular`(신청 많은순) · `feeLow`(참가비 낮은순), 없으면 최신순.
 * - 조건은 모두 주소에 두어 새로고침·뒤로가기(목록 복원)로 그대로 돌아온다.
 */

/** 목록 상단 탭 — 전체 · 명치사 커리어 · 일반 */
export type SeminarTypeTab = 'all' | 'career' | 'general';
/** 진행 방식 조건 — ONLINE 은 "일반 세미나 온라인 + 커리어 라이브·동영상" */
export type SeminarFormatFilter = 'OFFLINE' | 'LIVE' | 'VOD' | 'ONLINE';
export type SeminarSort = 'latest' | 'startSoon' | 'deadlineSoon' | 'popular' | 'feeLow';

export interface SeminarListQuery {
    tab: SeminarTypeTab;
    categories: string[];
    levels: string[];
    formats: SeminarFormatFilter[];
    freeOnly: boolean;
    sort: SeminarSort;
    /** 헤더 검색에서 넘어온 검색어 */
    search: string;
}

export interface SeminarOption<T extends string = string> {
    name: string;
    value: T;
}

export const SEMINAR_TYPE_TABS: { key: SeminarTypeTab; label: string }[] = [
    { key: 'all', label: '전체' },
    { key: 'career', label: '명치사 커리어' },
    { key: 'general', label: '일반' },
];

export const SEMINAR_LEVEL_OPTIONS: SeminarOption[] = [
    { name: '무관', value: 'ANY' },
    { name: '초급', value: 'BEGINNER' },
    { name: '중급', value: 'INTERMEDIATE' },
    { name: '고급', value: 'ADVANCED' },
];

export const SEMINAR_SORT_OPTIONS: SeminarOption<SeminarSort>[] = [
    { name: '최신순', value: 'latest' },
    { name: '시작 임박순', value: 'startSoon' },
    { name: '모집 마감 임박순', value: 'deadlineSoon' },
    { name: '신청 많은순', value: 'popular' },
    { name: '참가비 낮은순', value: 'feeLow' },
];

/** 주소·서버의 정렬 값 — 모르는 값은 최신순 */
export const parseSeminarSort = (raw: string | null): SeminarSort =>
    SEMINAR_SORT_OPTIONS.find((option: SeminarOption<SeminarSort>) => option.value === raw)?.value ?? 'latest';

const FORMAT_ORDER: SeminarFormatFilter[] = ['OFFLINE', 'ONLINE', 'LIVE', 'VOD'];
const FORMAT_NAMES: Record<SeminarFormatFilter, string> = {
    OFFLINE: '오프라인',
    ONLINE: '온라인',
    LIVE: '라이브',
    VOD: '동영상',
};

/**
 * 탭마다 고를 수 있는 진행 방식.
 * - 커리어: 오프라인·라이브·동영상(커리어의 "온라인" = 라이브+동영상이라 따로 두지 않는다)
 * - 일반: 오프라인·온라인(일반 세미나에는 라이브·동영상 구분이 없다)
 * - 전체: 넷 다(라이브·동영상은 커리어에만 있다는 안내와 함께)
 */
export const formatOptionsForTab = (tab: SeminarTypeTab): SeminarOption<SeminarFormatFilter>[] => {
    const values: SeminarFormatFilter[] = tab === 'career'
        ? ['OFFLINE', 'LIVE', 'VOD']
        : tab === 'general'
            ? ['OFFLINE', 'ONLINE']
            : FORMAT_ORDER;
    return values.map((value: SeminarFormatFilter) => ({ name: FORMAT_NAMES[value], value }));
};

export const formatName = (value: SeminarFormatFilter): string => FORMAT_NAMES[value];

const sortFormats = (formats: SeminarFormatFilter[]): SeminarFormatFilter[] =>
    FORMAT_ORDER.filter((value: SeminarFormatFilter) => formats.includes(value));

/**
 * 탭을 옮길 때 진행 방식을 새 탭에 맞게 바꾼다 — 뜻은 그대로 둔다.
 * 커리어로 가면 온라인 → 라이브+동영상, 일반으로 가면 라이브·동영상 → 온라인.
 */
export const normalizeFormatsForTab = (formats: SeminarFormatFilter[], tab: SeminarTypeTab): SeminarFormatFilter[] => {
    if (tab === 'career') {
        return sortFormats(formats.flatMap((value: SeminarFormatFilter): SeminarFormatFilter[] => (value === 'ONLINE' ? ['LIVE', 'VOD'] : [value])));
    }
    if (tab === 'general') {
        return sortFormats(formats.map((value: SeminarFormatFilter): SeminarFormatFilter => (value === 'LIVE' || value === 'VOD' ? 'ONLINE' : value)));
    }
    return sortFormats(formats);
};

const splitParam = (value: string | null): string[] =>
    (value ?? '').split(',').map((item: string) => item.trim()).filter(Boolean);

const isFormat = (value: string): value is SeminarFormatFilter => (FORMAT_ORDER as string[]).includes(value);

export const parseSeminarListQuery = (params: URLSearchParams): SeminarListQuery => {
    const type: string | null = params.get('type');
    const tab: SeminarTypeTab = type === 'career' || type === 'general' ? type : 'all';
    return {
        tab,
        categories: splitParam(params.get('category')),
        levels: splitParam(params.get('target')),
        formats: normalizeFormatsForTab(splitParam(params.get('format')).filter(isFormat), tab),
        freeOnly: params.get('free') === '1',
        sort: parseSeminarSort(params.get('sort')),
        search: params.get('search') ?? '',
    };
};

/** 조건을 주소 쿼리에 쓴다 — 목록 조건이 아닌 다른 파라미터는 그대로 둔다 */
export const writeSeminarListQuery = (base: URLSearchParams, query: SeminarListQuery): URLSearchParams => {
    const params: URLSearchParams = new URLSearchParams(base);
    const setOrDelete = (key: string, value: string): void => {
        if (value) params.set(key, value);
        else params.delete(key);
    };
    setOrDelete('type', query.tab === 'all' ? '' : query.tab);
    setOrDelete('category', query.categories.join(','));
    setOrDelete('target', query.levels.join(','));
    setOrDelete('format', query.formats.join(','));
    setOrDelete('free', query.freeOnly ? '1' : '');
    setOrDelete('sort', query.sort === 'latest' ? '' : query.sort);
    setOrDelete('search', query.search);
    // 예전 필터 박스가 열린 칸을 기억하던 값 — 더 쓰지 않는다
    params.delete('tab');
    return params;
};

/** 서버 목록 조회 파라미터(api.seminar.getList 의 filters) */
export const toSeminarApiFilters = (query: SeminarListQuery): Record<string, string> => {
    const filters: Record<string, string> = {};
    if (query.search) filters.search = query.search;
    if (query.categories.length > 0) filters.categories = query.categories.join(',');
    if (query.levels.length > 0) filters.levels = query.levels.join(',');
    if (query.formats.length > 0) filters.formats = query.formats.join(',');
    if (query.freeOnly) filters.freeOnly = 'true';
    if (query.tab === 'career') filters.seminarType = 'CAREER';
    if (query.tab === 'general') filters.seminarType = 'GENERAL';
    // 전체 탭은 어떤 정렬이든 커리어를 먼저 둔다
    if (query.tab === 'all') filters.sort = query.sort === 'latest' ? 'careerFirst' : `careerFirst,${query.sort}`;
    else if (query.sort !== 'latest') filters.sort = query.sort;
    return filters;
};

/** 같은 목록인지 가르는 문자열 — 목록 복원·재조회 판단용 */
export const seminarQueryKey = (query: SeminarListQuery): string =>
    [query.tab, query.categories.join(','), query.levels.join(','), query.formats.join(','), query.freeOnly ? '1' : '', query.sort, query.search].join('|');

/** [필터 N] 의 N — 고른 값 개수(정렬·검색어는 세지 않는다) */
export const countSeminarConditions = (query: SeminarListQuery): number =>
    query.categories.length + query.levels.length + query.formats.length + (query.freeOnly ? 1 : 0);

/** 카테고리·대상·진행 방식·무료만·검색어 중 하나라도 걸려 있는지 */
export const hasSeminarCondition = (query: SeminarListQuery): boolean =>
    countSeminarConditions(query) > 0 || Boolean(query.search);

/** 조건(정렬·탭은 두고)을 모두 푼다 */
export const clearSeminarConditions = (query: SeminarListQuery): SeminarListQuery => ({
    ...query,
    categories: [],
    levels: [],
    formats: [],
    freeOnly: false,
    search: '',
});

// ───────── 조건 저장 (common/savedListFilter) ─────────

/** 저장하는 조건 — 탭은 저장하고(사용자 결정), 정렬·검색어는 저장하지 않는다 */
const toSavableSeminarQuery = (query: SeminarListQuery): SeminarListQuery => ({ ...query, sort: 'latest', search: '' });

/** 저장용 주소 쿼리 — 저장한 조건과 지금 조건이 같은지도 이 문자열로 견준다 */
export const savedSeminarQueryString = (query: SeminarListQuery): string =>
    writeSeminarListQuery(new URLSearchParams(), toSavableSeminarQuery(query)).toString();

/** 저장한 주소 쿼리 → 조건 */
export const parseSavedSeminarQuery = (savedQuery: string): SeminarListQuery =>
    parseSeminarListQuery(new URLSearchParams(savedQuery));

/** 저장할 목록 API 인자(정렬·검색어 제외) — 서버가 새 세미나를 이 조건에 맞춰 본다 */
export const savedSeminarParams = (query: SeminarListQuery): Record<string, string[]> => {
    const filters: Record<string, string> = toSeminarApiFilters(toSavableSeminarQuery(query));
    return Object.fromEntries(
        Object.entries(filters)
            .filter(([key]: [string, string]) => key !== 'sort' && key !== 'search')
            .map(([key, value]: [string, string]): [string, string[]] => [key, value.split(',')]),
    );
};

/** 조건 없이 들어온 주소인지 — 이때만 저장한 조건을 켠다(주소에 조건이 있으면 주소가 먼저). 탭·정렬만 있는 주소는 조건 없는 주소다 */
export const isPlainSeminarEntry = (params: URLSearchParams): boolean => !hasSeminarCondition(parseSeminarListQuery(params));

/** 들어온 탭에서 저장한 조건을 켜도 되는지 — 탭을 골라 들어왔는데(커리어·일반) 저장한 탭이 다르면 켜지 않는다. 전체 탭은 메뉴의 기본 주소라 어느 탭이든 켠다 */
export const savedSeminarFitsEntry = (entry: SeminarListQuery, saved: SeminarListQuery): boolean =>
    entry.tab === 'all' || entry.tab === saved.tab;
